import React, { useState, useEffect } from "react";
import { observer } from "mobx-react-lite";
import { Tabs, Tab, Grid, TextField, Button, Box, Dialog, DialogContent, DialogActions, Typography, List, ListItemButton, ListItemText } from "@mui/material";
import additionalInfo from "../../../Mobx/AdditionalInfo";
import dictionary from "../../../Mobx/Dictionary";
import InfoTooltip from "./InfoTooltip";
import LoadingIndicator from "../../SharedArea/Loading/LoadingIndicator";
import { replacePlaceholders } from "../../../Utils/additionalInfo";

const LOCAL_STORAGE_KEY_FOR_TAB = "selectedTab";
const LOCAL_STORAGE_KEY_FOR_PROMPT = "selectedPromptId"; // + _{selectedTab}

const Prompt = observer(({ height }) => {
    const [selectedTab, setSelectedTab] = useState(localStorage.getItem(LOCAL_STORAGE_KEY_FOR_TAB) || "sentence");
    const [editedPrompts, setEditedPrompts] = useState({});
    const [editedName, setEditedName] = useState({});
    const [processedTexts, setProcessedTexts] = useState({});
    const [selectedPromptId_forListItem, setSelectedPromptId_forListItem] = useState(null);
    const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
    const [promptToDelete, setPromptToDelete] = useState(null);

    const currentWord = dictionary.getOne(dictionary.current.value._id);

    useEffect(() => {
        const savedPromptId = localStorage.getItem(`${LOCAL_STORAGE_KEY_FOR_PROMPT}_${selectedTab}`);
        setSelectedPromptId_forListItem(savedPromptId || additionalInfo.aiSettings.text.prompts.selected[selectedTab]);
    }, [selectedTab]);

    const handleTabChange = (event, newValue) => {
        setSelectedTab(newValue);
        const savedPromptId = localStorage.getItem(`${LOCAL_STORAGE_KEY_FOR_PROMPT}_${newValue}`);
        setSelectedPromptId_forListItem(savedPromptId || additionalInfo.aiSettings.text.prompts.selected[newValue]);
        localStorage.setItem(LOCAL_STORAGE_KEY_FOR_TAB, newValue);
    };

    const handleListItemClick = (promptId) => {
        setSelectedPromptId_forListItem(promptId);
        localStorage.setItem(`${LOCAL_STORAGE_KEY_FOR_PROMPT}_${selectedTab}`, promptId);
    };

    const handleInputChange = (promptId, value) => {
        setEditedPrompts((prev) => ({ ...prev, [promptId]: value }));

        if (currentWord) {
            const newValue = replacePlaceholders(value, currentWord);
            setProcessedTexts((prev) => ({ ...prev, [promptId]: newValue }));
        }
    };

    const handleNameChange = (promptId, value) => {
        setEditedName((prev) => ({ ...prev, [promptId]: value }));
    };

    const handleUpdatePrompt = async (promptId) => {
        const currentPrompt = additionalInfo.aiSettings.text.prompts.options[selectedTab].find(p => p._id === promptId);
        const newPrompt = editedPrompts[promptId] || currentPrompt.prompt;
        const newName = editedName[promptId] || currentPrompt.name;
        await additionalInfo.updatePrompt("text", selectedTab, promptId, newPrompt, newName);
    };

    const handleDeletePrompt = (promptId) => {
        setPromptToDelete(promptId);
        setOpenConfirmDialog(true);
    };

    const confirmDeletePrompt = async () => {
        if (promptToDelete) {
            await additionalInfo.deletePrompt("text", selectedTab, promptToDelete);
            setSelectedPromptId_forListItem(additionalInfo.aiSettings.text.prompts.selected[selectedTab]);
            localStorage.removeItem(`${LOCAL_STORAGE_KEY_FOR_PROMPT}_${selectedTab}`);
        }
        setOpenConfirmDialog(false);
        setPromptToDelete(null);
    };

    const handleCreatePrompt = async () => {
        await additionalInfo.createPrompt("text", selectedTab, "New Prompt", "שם הפרומפט");
    };

    // For Tabs ______________________________________________________
    // Ordered tabs for rendering
    const orderedTabs = [
        "imageDescription",
        "image",
        "sentence",
        "definition",
        "story",
        "trnsAr",
        "trnsRu",
        "distractingForTrns",
        "distractingForOriginal",
        "distractingForDefinitionInEng",
        "distractingForDefinitionInHeb",
        "distractingForWordFamilies",
        "sentencesWithMissing",
        "sentencesWithLastMissing",
        "opposites",
        "synonyms"
    ];

    // Convert plural to singular for tab names
    const pluralToSingular = {
        images: "image",
        imageDescription: "imageDescription",
        sentences: "sentence",
        definitions: "definition",
        stories: "story",
        distractingForTrns: "distractingForTrns",
        distractingForOriginal: "distractingForOriginal",
        distractingForDefinitionInEng: "distractingForDefinitionInEng",
        distractingForDefinitionInHeb: "distractingForDefinitionInHeb",
        distractingForWordFamilies: "distractingForWordFamilies",
        sentencesWithMissing: "sentencesWithMissing",
        sentencesWithLastMissing: "sentencesWithLastMissing",
        opposites: "opposites",
        synonyms: "synonyms",
        trnsAr: "תרגום לערבית",
        trnsRu: "תרגום לרוסית"
    };

    // Tab labels in Hebrew
    const tabLabels = {
        imageDescription: "תיאור תמונות",
        image: "תמונות",
        sentence: "משפטים",
        definition: "הגדרות",
        story: "סיפורים",
        distractingForTrns: "מסיחים עבור תרגום",
        distractingForOriginal: "מסיחים עבור אוריגינאל",
        distractingForDefinitionInEng: "מסיחים עבור הגדרות באנגלית",
        distractingForDefinitionInHeb: "מסיחים עבור הגדרות בעברית",
        distractingForWordFamilies: "מסיחים עבור משפחות מילים",
        sentencesWithMissing: "משפטים שחסר בהם מילה באמצע",
        sentencesWithLastMissing: "משפטים שחסר בהם מילה בסוף",
        opposites: "הפכים וזוגות",
        synonyms: "מילים נרדפות",
        trnsAr: "תרגום לערבית",
        trnsRu: "תרגום לרוסית"
    };

    const baseSections = [...additionalInfo.sectionsToShow];

    // If the user has selected "images", add "imageDescription" to the list of sections to show
    if (baseSections.includes("images") && !baseSections.includes("imageDescription")) {
        baseSections.push("imageDescription");
    }

    // Extracting singular values ​​from the list of tabs in a specified order
    const tabsToRender = orderedTabs.filter(
        section => baseSections.includes(section) || baseSections.includes(Object.keys(pluralToSingular).find(key => pluralToSingular[key] === section))
    );

    // ______________________________________________________________
    return (
        <div>
            {/* Tabs */}
            <Tabs
                value={selectedTab}
                onChange={handleTabChange}
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                    minHeight: "37px",
                    height: "37px",
                    maxWidth: "75%",
                    overflowX: "auto",
                }}
            >
                {tabsToRender.map((section) => (
                    <Tab
                        key={section}
                        label={tabLabels[section]}
                        value={section}
                        sx={{ minHeight: "32px", height: "32px", padding: "4px 8px" }}
                    />
                ))}
            </Tabs>

            {/* Toolbar */}
            <Box sx={{ position: "absolute", top: -10, right: 75, display: "flex", alignItems: "center", gap: 0.5 }}>
                <LoadingIndicator isLoading={additionalInfo.loading.aiSettings} />
                <Button variant="contained" onClick={handleCreatePrompt} sx={{ mt: 2 }}>
                    צור פרומפט
                </Button>
                <InfoTooltip />
            </Box>

            {/* Prompts */}
            <Box sx={{ display: "flex", maxHeight: height ? `${height - 60}px` : "500px", minHeight: height ? `${height - 60}px` : "500px", mt: 0.5 }}>
                {/* רשימת שמות הפרומפטים (צד ימין) */}
                <Box
                    sx={{
                        width: "20%",
                        // borderRight: "2px solid #1976d2", 
                        overflowY: "auto", direction: "rtl", p: 0
                    }}
                >
                    <List sx={{ padding: 0 }}>
                        {additionalInfo.aiSettings.text.prompts.options[selectedTab].map((prompt) => (
                            <ListItemButton
                                key={prompt._id}
                                selected={selectedPromptId_forListItem === prompt._id}
                                onClick={() => handleListItemClick(prompt._id)}
                                sx={{
                                    padding: 0,
                                    pl: 0,
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    color: prompt.default ? "gray" : "black",
                                    borderRadius: "4px",
                                    border: "1px solid white",
                                    backgroundColor: "#f5f5f5",
                                    "&.Mui-selected": {
                                        backgroundColor: "#d6d6d6 !important",
                                        // border: "1px solid #757575",
                                    },
                                }}
                            >
                                <ListItemText primary={prompt.name} />
                                <Button
                                    variant="contained"
                                    color={additionalInfo.aiSettings.text.prompts.selected[selectedTab] === prompt._id ? "warning" : "primary"}
                                    onClick={async (e) => {
                                        e.stopPropagation(); // מונע שינוי התצוגה
                                        await additionalInfo.setSelectedPrompt("text", selectedTab, prompt._id);
                                    }}
                                    size="small"
                                    sx={{ minWidth: "60px", marginRight: "8px", marginLeft: "0px" }}
                                >
                                    {additionalInfo.aiSettings.text.prompts.selected[selectedTab] === prompt._id ? "נבחר" : "בחירה"}
                                </Button>
                            </ListItemButton>
                        ))}
                    </List>
                </Box>

                {/* הצגת הפרומפט הנבחר (צד שמאל) */}
                <Box sx={{ width: "80%", pl: 1.5, pb: 0.5, overflowY: "auto" }}>
                    {selectedPromptId_forListItem && (
                        (() => {
                            const prompt = additionalInfo.aiSettings.text.prompts.options[selectedTab].find(p => p._id === selectedPromptId_forListItem);
                            if (!prompt) return null;

                            const isDefault = prompt.default;
                            const isEmpty = editedPrompts[prompt._id] === "";

                            return (
                                <Grid
                                    container
                                    direction="column"
                                    alignItems="center"
                                    justifyContent="center"
                                    spacing={1}
                                    sx={{
                                        my: 0,
                                        width: "100%",
                                        px: 1,
                                        py: 0,
                                        borderRadius: "8px",
                                        // border: "1px solid #9e9e9e !important",
                                        backgroundColor: "#f5f5f5",
                                    }}
                                >
                                    {/* כפתורים ושם הפרומפט */}
                                    <Grid item sx={{ display: "flex", gap: 1, justifyContent: "center" }}>
                                        <TextField
                                            size="small"
                                            label="שם הפרומפט"
                                            disabled={isDefault}
                                            value={editedName[prompt._id] !== undefined ? editedName[prompt._id] : prompt.name}
                                            onChange={(e) => handleNameChange(prompt._id, e.target.value)}
                                            sx={{
                                                width: "100%",
                                                direction: /^[֐-׿]/.test(editedName[prompt._id]?.trim() || prompt.name) ? "ltr" : "rtl",
                                            }}
                                        />
                                        <Button
                                            variant="contained"
                                            color="success"
                                            onClick={() => handleUpdatePrompt(prompt._id)}
                                            disabled={isDefault || isEmpty}
                                        >
                                            עדכון
                                        </Button>
                                        <Button
                                            variant="contained"
                                            color="error"
                                            onClick={() => handleDeletePrompt(prompt._id)}
                                            disabled={isDefault}
                                        >
                                            מחיקה
                                        </Button>
                                    </Grid>

                                    {/* שדה קלט */}
                                    <Grid container spacing={0} sx={{ mt: 0.8 }}>
                                        <TextField
                                            label="פרומפט"
                                            disabled={isDefault}
                                            fullWidth
                                            multiline
                                            value={editedPrompts[prompt._id] !== undefined ? editedPrompts[prompt._id] : prompt.prompt}
                                            onChange={(e) => handleInputChange(prompt._id, e.target.value)}
                                            sx={{
                                                width: "100%",
                                                direction: /^[֐-׿]/.test(editedPrompts[prompt._id]?.trim() || prompt.prompt) ? "ltr" : "rtl",
                                                "& .MuiInputBase-input.Mui-disabled": {
                                                    WebkitTextFillColor: "gray",
                                                },
                                                "& .MuiOutlinedInput-notchedOutline": {
                                                    borderColor: "black !important",
                                                },
                                                backgroundColor: "white",
                                            }}
                                        />
                                    </Grid>

                                    {/* הצגת הטקסט המעובד */}
                                    <Grid item sx={{ width: "100%", textAlign: "center" }}>
                                        <Typography variant="body2" sx={{ color: "text.secondary", marginBottom: 0, marginTop: -0.5 }}>
                                            {currentWord ? (
                                                <>
                                                    👇&nbsp;&nbsp;כך יראה הפרומפט עבור הערך:&nbsp;
                                                    {currentWord.original}, {currentWord.trns}, {currentWord.pos}&nbsp;&nbsp;👇
                                                </>
                                            ) : (
                                                "בחר ערך מהמילון כדי לראות איך הפרומפט יראה"
                                            )}
                                        </Typography>

                                        <Box
                                            sx={{
                                                width: "100%",
                                                direction: /^[֐-׿]/.test(editedPrompts[prompt._id]?.trim() || prompt.prompt?.trim()) ? "ltr" : "rtl",
                                                textAlign: /^[֐-׿]/.test(editedPrompts[prompt._id]?.trim() || prompt.prompt?.trim()) ? "left" : "right",
                                            }}
                                        >
                                            <Typography
                                                variant="body1"
                                                sx={{
                                                    width: "100%",
                                                    whiteSpace: "pre-wrap",
                                                    border: "1px solid #f5f5f5",
                                                    borderRadius: "4px",
                                                    padding: "8px",
                                                    backgroundColor: "#f5f5f5",
                                                    paddingTop: 0.1,
                                                }}
                                            >
                                                {
                                                    processedTexts[prompt._id] ||
                                                    replacePlaceholders(prompt.prompt, currentWord)
                                                }
                                            </Typography>
                                        </Box>
                                    </Grid>
                                </Grid>
                            );
                        })()
                    )}
                </Box>
            </Box>

            {/* דיאלוג אישור מחיקה */}
            <Dialog open={openConfirmDialog} onClose={() => setOpenConfirmDialog(false)}>
                <DialogContent>
                    <Typography>האם אתה בטוח שברצונך למחוק את הפרומפט?</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenConfirmDialog(false)} color="primary">
                        ביטול
                    </Button>
                    <Button onClick={confirmDeletePrompt} color="error" variant="contained">
                        מחיקה
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
});

export default Prompt;
